import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationBoolean,
    requirePresentationFields,
    requirePresentationFinite,
    requirePresentationNullableString,
    requirePresentationRecord,
    requirePresentationString,
    parseCalculatorSourceMainGroupPresentation,
} from "./calculator-presentation-contract.js";
import {
    calculatorLootEvidenceSortValue,
    calculatorLootRowSortValues,
} from "./calculator-loot-sort.js";
import {
    createLootSankeyProjectionContext,
    createLootSankeyProjectorCache,
} from "./loot-sankey-projector-context.js";
import {
    calculatorLootItemRateDisplayForMode,
    calculatorLootRateDisplay,
    calculatorLootRateDisplayForMode,
    parseCalculatorLootRateDisplayMode,
} from "./calculator-loot-rate-presentation.js";
import { calculatorMetricUnitMarkForValueField } from "./calculator-metric-unit-mark.js";
import { calculatorItemQuantityDisplay } from "./calculator-item-quantity-presentation.js";

const RATE_BASES = Object.freeze([
    "ratio",
    "percent",
    "denormalizedWeight",
    "rawWeight",
    "perCatch",
    "perHour",
]);
const NUMERIC_STATES = new Set(["known", "unknown", "unavailable"]);
const TRACE_STATUSES = new Set(["effective", "overridden", "refuted", "inactiveCondition"]);
const RIGHT_METRICS = Object.freeze({
    profit: Object.freeze({
        ariaLabel: "ariaLabel",
        field: "expectedProfit",
        format: "silver",
        label: "profitMetricLabel",
        unit: "silver",
    }),
    fishingExperience: Object.freeze({
        ariaLabel: "fishingExperienceAriaLabel",
        field: "expectedFishingExperience",
        format: "experience",
        label: "fishingExperienceMetricLabel",
        unit: "experience",
    }),
    totemExperience: Object.freeze({
        ariaLabel: "totemExperienceAriaLabel",
        field: "expectedTotemExperience",
        format: "experience",
        label: "totemExperienceMetricLabel",
        unit: "experience",
    }),
});

function requireRecord(value, path) {
    return requirePresentationRecord(value, path);
}

function requireString(record, key, path) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
        throw new CalculatorPresentationContractError(`${path}.${key}`, "missing field");
    }
    return requirePresentationString(record[key], `${path}.${key}`);
}

function requirePositiveInteger(value, path) {
    if (!Number.isInteger(value) || value <= 0) {
        throw new CalculatorPresentationContractError(path, "expected a positive integer");
    }
    return value;
}

function parseQuantifiedRate(value, path) {
    const rate = requirePresentationFields(value, ["basis", "value"], path);
    if (!RATE_BASES.includes(rate.basis)) {
        throw new CalculatorPresentationContractError(`${path}.basis`, "unsupported rate basis");
    }
    return {
        value: requirePresentationFinite(rate.value, `${path}.value`, { minimum: 0 }),
        basis: rate.basis,
    };
}

function parseStatefulRate(value, path, knownBasis, label) {
    const record = requireRecord(value, path);
    if (!NUMERIC_STATES.has(record.state)) {
        throw new CalculatorPresentationContractError(`${path}.state`, "unsupported rate state");
    }
    if (record.state === "known") {
        const known = requirePresentationFields(record, ["basis", "state", "value"], path);
        if (known.basis !== knownBasis) {
            throw new CalculatorPresentationContractError(
                `${path}.basis`,
                `known ${label} must use ${knownBasis} basis`,
            );
        }
        return {
            state: "known",
            value: requirePresentationFinite(known.value, `${path}.value`, { minimum: 0 }),
            basis: known.basis,
        };
    }
    if (record.state === "unknown") {
        requirePresentationFields(record, ["state"], path);
        return { state: "unknown" };
    }
    const unavailable = requirePresentationFields(record, ["reason", "state"], path);
    const reasonPath = `${path}.reason`;
    const reason = requirePresentationFields(
        unavailable.reason,
        ["code", "detailCode", "sourceId"],
        reasonPath,
    );
    return {
        state: "unavailable",
        reason: {
            code: requirePresentationString(reason.code, `${reasonPath}.code`),
            detailCode: requirePresentationNullableString(
                reason.detailCode,
                `${reasonPath}.detailCode`,
            ),
            sourceId: requirePresentationNullableString(
                reason.sourceId,
                `${reasonPath}.sourceId`,
            ),
        },
    };
}

function parseRawWeightRate(value, path) {
    return parseStatefulRate(value, path, "rawWeight", "raw weight");
}

function parseDenormalizedRate(value, path) {
    return parseStatefulRate(value, path, "percent", "denormalized rate");
}

function parseNumericValue(value, path) {
    const record = requireRecord(value, path);
    if (!NUMERIC_STATES.has(record.state)) {
        throw new CalculatorPresentationContractError(`${path}.state`, "unsupported numeric state");
    }
    if (record.state === "known") {
        const known = requirePresentationFields(record, ["state", "unit", "value"], path);
        return {
            state: "known",
            unit: requirePresentationString(known.unit, `${path}.unit`),
            value: requirePresentationFinite(known.value, `${path}.value`, { minimum: 0 }),
        };
    }
    if (record.state === "unknown") {
        requirePresentationFields(record, ["state"], path);
        return { state: "unknown" };
    }
    const unavailable = requirePresentationFields(record, ["reason", "state"], path);
    requireRecord(unavailable.reason, `${path}.reason`);
    return { state: "unavailable" };
}

function numericWeight(value) {
    return value.state === "known" ? value.value : null;
}

function parseNumberFormat(value, path) {
    const format = requirePresentationFields(
        value,
        ["maximumFractionDigits", "minimumFractionDigits"],
        path,
    );
    const minimumFractionDigits = format.minimumFractionDigits;
    const maximumFractionDigits = format.maximumFractionDigits;
    if (
        !Number.isInteger(minimumFractionDigits)
        || !Number.isInteger(maximumFractionDigits)
        || minimumFractionDigits < 0
        || maximumFractionDigits > 20
        || minimumFractionDigits > maximumFractionDigits
    ) {
        throw new CalculatorPresentationContractError(
            path,
            "fraction digits must be integers from 0 through 20 in ascending order",
        );
    }
    return { minimumFractionDigits, maximumFractionDigits };
}

function parseLabels(value) {
    const path = "presentation.labels";
    const labels = requirePresentationFields(
        value,
        [
            "ariaLabel",
            "basePriceLabel",
            "countMetricLabel",
            "fishingExperienceAriaLabel",
            "fishingExperienceMetricLabel",
            "profitMetricLabel",
            "provenanceLabel",
            "rateBasisLabels",
            "unavailableValueLabel",
            "unknownItemLabel",
            "unknownValueLabel",
            "totemExperienceMetricLabel",
            "totemExperienceAriaLabel",
        ],
        path,
    );
    const rateBasisLabels = requirePresentationFields(
        labels.rateBasisLabels,
        RATE_BASES,
        `${path}.rateBasisLabels`,
    );
    return Object.freeze({
        ariaLabel: requirePresentationString(labels.ariaLabel, `${path}.ariaLabel`),
        basePriceLabel: requirePresentationString(
            labels.basePriceLabel,
            `${path}.basePriceLabel`,
        ),
        provenanceLabel: requirePresentationString(
            labels.provenanceLabel,
            `${path}.provenanceLabel`,
        ),
        countMetricLabel: requirePresentationString(
            labels.countMetricLabel,
            `${path}.countMetricLabel`,
        ),
        profitMetricLabel: requirePresentationString(
            labels.profitMetricLabel,
            `${path}.profitMetricLabel`,
        ),
        fishingExperienceMetricLabel: requirePresentationString(
            labels.fishingExperienceMetricLabel,
            `${path}.fishingExperienceMetricLabel`,
        ),
        fishingExperienceAriaLabel: requirePresentationString(
            labels.fishingExperienceAriaLabel,
            `${path}.fishingExperienceAriaLabel`,
        ),
        totemExperienceMetricLabel: requirePresentationString(
            labels.totemExperienceMetricLabel,
            `${path}.totemExperienceMetricLabel`,
        ),
        totemExperienceAriaLabel: requirePresentationString(
            labels.totemExperienceAriaLabel,
            `${path}.totemExperienceAriaLabel`,
        ),
        unknownValueLabel: requirePresentationString(
            labels.unknownValueLabel,
            `${path}.unknownValueLabel`,
        ),
        unknownItemLabel: requirePresentationString(
            labels.unknownItemLabel,
            `${path}.unknownItemLabel`,
        ),
        unavailableValueLabel: requirePresentationString(
            labels.unavailableValueLabel,
            `${path}.unavailableValueLabel`,
        ),
        rateBasisLabels: Object.freeze(Object.fromEntries(
            RATE_BASES.map((basis) => [
                basis,
                requirePresentationString(
                    rateBasisLabels[basis],
                    `${path}.rateBasisLabels.${basis}`,
                    { allowEmpty: true },
                ),
            ]),
        )),
    });
}

function parseStaticGroupPresentation(table, groupId) {
    const tablePath = "presentation.groupsByGroupId";
    const values = requireRecord(table, tablePath);
    if (!Object.prototype.hasOwnProperty.call(values, groupId)) {
        throw new CalculatorPresentationContractError(tablePath, `missing group ${groupId}`);
    }
    const path = `${tablePath}.${groupId}`;
    const value = requirePresentationFields(
        values[groupId],
        [
            "connectorColor",
            "fillColor",
            "label",
            "sourceMainGroup",
            "strokeColor",
            "textColor",
        ],
        path,
    );
    return {
        label: requirePresentationString(value.label, `${path}.label`),
        sourceMainGroup: parseCalculatorSourceMainGroupPresentation(
            value.sourceMainGroup,
            `${path}.sourceMainGroup`,
        ),
        fillColor: requirePresentationString(value.fillColor, `${path}.fillColor`),
        strokeColor: requirePresentationString(value.strokeColor, `${path}.strokeColor`),
        textColor: requirePresentationString(value.textColor, `${path}.textColor`),
        connectorColor: requirePresentationString(
            value.connectorColor,
            `${path}.connectorColor`,
        ),
    };
}

function parseStaticItemStyle(value, path) {
    const style = requirePresentationFields(
        value,
        [
            "connectorColor",
            "fillColor",
            "gradeRank",
            "iconGradeTone",
            "strokeColor",
            "textColor",
        ],
        path,
    );
    return {
        iconGradeTone: requirePresentationString(style.iconGradeTone, `${path}.iconGradeTone`),
        gradeRank: requirePresentationFinite(style.gradeRank, `${path}.gradeRank`, {
            minimum: 0,
        }),
        fillColor: requirePresentationString(style.fillColor, `${path}.fillColor`),
        strokeColor: requirePresentationString(style.strokeColor, `${path}.strokeColor`),
        textColor: requirePresentationString(style.textColor, `${path}.textColor`),
        connectorColor: requirePresentationString(
            style.connectorColor,
            `${path}.connectorColor`,
        ),
    };
}

function parseStaticItemPresentation(table, itemId, unknownItem, unknownItemLabel) {
    const tablePath = "presentation.itemsByItemId";
    const values = requireRecord(table, tablePath);
    const key = String(itemId);
    if (!Object.prototype.hasOwnProperty.call(values, key)) {
        return {
            label: `${unknownItemLabel} ${itemId}`,
            iconUrl: null,
            ...unknownItem,
        };
    }
    const path = `${tablePath}.${key}`;
    const value = requirePresentationFields(
        values[key],
        [
            "connectorColor",
            "fillColor",
            "gradeRank",
            "iconGradeTone",
            "iconUrl",
            "label",
            "strokeColor",
            "textColor",
        ],
        path,
    );
    return {
        label: requirePresentationString(value.label, `${path}.label`),
        iconUrl: requirePresentationNullableString(value.iconUrl, `${path}.iconUrl`),
        iconGradeTone: requirePresentationString(value.iconGradeTone, `${path}.iconGradeTone`),
        gradeRank: requirePresentationFinite(value.gradeRank, `${path}.gradeRank`, {
            minimum: 0,
        }),
        fillColor: requirePresentationString(value.fillColor, `${path}.fillColor`),
        strokeColor: requirePresentationString(value.strokeColor, `${path}.strokeColor`),
        textColor: requirePresentationString(value.textColor, `${path}.textColor`),
        connectorColor: requirePresentationString(
            value.connectorColor,
            `${path}.connectorColor`,
        ),
    };
}

function parsePresentation(value) {
    const presentation = requirePresentationFields(
        value,
        [
            "conditionsByOperationId",
            "groupsByGroupId",
            "itemsByItemId",
            "labels",
            "locale",
            "numberFormats",
            "provenance",
            "unknownItem",
        ],
        "presentation",
    );
    const locale = requirePresentationString(presentation.locale, "presentation.locale");
    try {
        new Intl.NumberFormat(locale);
    } catch (_error) {
        throw new CalculatorPresentationContractError("presentation.locale", "invalid locale");
    }
    const formats = requirePresentationFields(
        presentation.numberFormats,
        ["count", "experience", "rate", "silver"],
        "presentation.numberFormats",
    );
    const provenance = requirePresentationFields(
        presentation.provenance,
        ["evidenceClasses", "factKinds", "statuses"],
        "presentation.provenance",
    );
    return {
        locale,
        labels: parseLabels(presentation.labels),
        numberFormats: {
            count: parseNumberFormat(formats.count, "presentation.numberFormats.count"),
            experience: parseNumberFormat(
                formats.experience,
                "presentation.numberFormats.experience",
            ),
            rate: parseNumberFormat(formats.rate, "presentation.numberFormats.rate"),
            silver: parseNumberFormat(formats.silver, "presentation.numberFormats.silver"),
        },
        groupsByGroupId: requireRecord(
            presentation.groupsByGroupId,
            "presentation.groupsByGroupId",
        ),
        conditionsByOperationId: Object.freeze(Object.fromEntries(
            Object.entries(requireRecord(
                presentation.conditionsByOperationId,
                "presentation.conditionsByOperationId",
            )).map(([operationId, conditionText]) => [
                requirePresentationString(
                    operationId,
                    "presentation.conditionsByOperationId key",
                ),
                requirePresentationString(
                    conditionText,
                    `presentation.conditionsByOperationId.${operationId}`,
                ),
            ]),
        )),
        itemsByItemId: requireRecord(presentation.itemsByItemId, "presentation.itemsByItemId"),
        unknownItem: parseStaticItemStyle(presentation.unknownItem, "presentation.unknownItem"),
        provenance: {
            evidenceClasses: requireRecord(
                provenance.evidenceClasses,
                "presentation.provenance.evidenceClasses",
            ),
            factKinds: requireRecord(provenance.factKinds, "presentation.provenance.factKinds"),
            statuses: requireRecord(provenance.statuses, "presentation.provenance.statuses"),
        },
    };
}

function formatLootRateForMode(
    normalizedRate,
    denormalizedRate,
    rawWeight,
    mode,
    presentation,
    { itemRate = false } = {},
) {
    const display = itemRate
        ? calculatorLootItemRateDisplayForMode
        : calculatorLootRateDisplayForMode;
    return display(
        normalizedRate,
        denormalizedRate,
        rawWeight,
        mode,
        {
            locale: presentation.locale,
            numberFormat: presentation.numberFormats.rate,
            unknownText: presentation.labels.unknownValueLabel,
            unavailableText: presentation.labels.unavailableValueLabel,
        },
    );
}

function formatNumeric(value, expectedUnit, formatter, labels, path) {
    if (value.state === "unknown") {
        return labels.unknownValueLabel;
    }
    if (value.state === "unavailable") {
        return labels.unavailableValueLabel;
    }
    if (value.unit !== expectedUnit) {
        throw new CalculatorPresentationContractError(
            `${path}.unit`,
            `expected ${expectedUnit}`,
        );
    }
    return formatter.format(value.value);
}

function formatMetricShare(value, presentation, path) {
    if (value.state === "unknown") {
        return calculatorLootRateDisplay(
            { state: "unknown" },
            { unknownText: presentation.labels.unknownValueLabel },
        );
    }
    if (value.state === "unavailable") {
        return calculatorLootRateDisplay(
            { state: "unavailable" },
            { unavailableText: presentation.labels.unavailableValueLabel },
        );
    }
    if (value.unit !== "ratio") {
        throw new CalculatorPresentationContractError(`${path}.unit`, "expected ratio");
    }
    return calculatorLootRateDisplay(
        { state: "known", value: value.value, basis: "ratio" },
        {
            locale: presentation.locale,
            numberFormat: {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            },
            unknownText: presentation.labels.unknownValueLabel,
            unavailableText: presentation.labels.unavailableValueLabel,
        },
    );
}

function formatCompactNumeric(value, expectedUnit, locale, labels, path) {
    if (value.state === "unknown") {
        return labels.unknownValueLabel;
    }
    if (value.state === "unavailable") {
        return labels.unavailableValueLabel;
    }
    if (value.unit !== expectedUnit) {
        throw new CalculatorPresentationContractError(
            `${path}.unit`,
            `expected ${expectedUnit}`,
        );
    }
    return new Intl.NumberFormat(locale, value.value < 1_000
        ? { maximumFractionDigits: 0 }
        : { notation: "compact", maximumFractionDigits: 1 }).format(value.value);
}

function metricPresentationFor(metricPresentations, kind, rowId, metric, path) {
    const rows = metricPresentations?.[kind];
    if (!(rows instanceof Map)) {
        throw new CalculatorPresentationContractError(
            `metricPresentations.${kind}`,
            "expected a Map",
        );
    }
    const metrics = rows.get(rowId);
    if (!(metrics instanceof Map)) {
        throw new CalculatorPresentationContractError(path, `missing metric row ${rowId}`);
    }
    const value = metrics.get(metric);
    if (!value) {
        throw new CalculatorPresentationContractError(path, `missing metric ${metric}`);
    }
    const record = requirePresentationFields(value, ["breakdown", "share"], path);
    return {
        breakdown: requireRecord(record.breakdown, `${path}.breakdown`),
        share: parseNumericValue(record.share, `${path}.share`),
    };
}

function itemPriceStatesByItemId(projection) {
    const states = new Map();
    requirePresentationArray(
        projection?.provenanceTraces,
        "projection.provenanceTraces",
    ).forEach((trace, traceIndex) => {
        const tracePath = `projection.provenanceTraces[${traceIndex}]`;
        const traceRecord = requireRecord(trace, tracePath);
        const factKey = requireRecord(traceRecord.factKey, `${tracePath}.factKey`);
        if (requireString(factKey, "kind", `${tracePath}.factKey`) !== "itemPrice") {
            return;
        }
        const itemId = requirePositiveInteger(
            factKey.itemId,
            `${tracePath}.factKey.itemId`,
        );
        if (states.has(itemId)) {
            throw new CalculatorPresentationContractError(
                `${tracePath}.factKey.itemId`,
                `duplicate item price trace for ${itemId}`,
            );
        }
        let effective = null;
        let effectivePath = null;
        requirePresentationArray(traceRecord.candidates, `${tracePath}.candidates`)
            .forEach((candidate, candidateIndex) => {
                const candidatePath = `${tracePath}.candidates[${candidateIndex}]`;
                const candidateRecord = requireRecord(candidate, candidatePath);
                const status = requireString(candidateRecord, "status", candidatePath);
                if (!TRACE_STATUSES.has(status)) {
                    throw new CalculatorPresentationContractError(
                        `${candidatePath}.status`,
                        "unsupported status",
                    );
                }
                if (status !== "effective") {
                    return;
                }
                if (effective) {
                    throw new CalculatorPresentationContractError(
                        `${tracePath}.candidates`,
                        "item price trace has multiple effective candidates",
                    );
                }
                effective = candidateRecord;
                effectivePath = candidatePath;
            });
        if (!effective) {
            states.set(itemId, Object.freeze({ state: "unknown" }));
            return;
        }
        const operation = requireRecord(effective.operation, `${effectivePath}.operation`);
        if (requireString(operation, "kind", `${effectivePath}.operation`) !== "itemPrice") {
            throw new CalculatorPresentationContractError(
                `${effectivePath}.operation.kind`,
                "expected itemPrice",
            );
        }
        const operationItemId = requirePositiveInteger(
            operation.itemId,
            `${effectivePath}.operation.itemId`,
        );
        if (operationItemId !== itemId) {
            throw new CalculatorPresentationContractError(
                `${effectivePath}.operation.itemId`,
                `does not match fact key item ${itemId}`,
            );
        }
        states.set(
            itemId,
            Object.freeze(parseNumericValue(operation.price, `${effectivePath}.operation.price`)),
        );
    });
    return states;
}

export function createCalculatorItemPriceStateResolver(projection) {
    const priceStates = itemPriceStatesByItemId(projection);
    return Object.freeze({
        resolve(itemId, path = `projection.itemPrices.${itemId}`) {
            const id = requirePositiveInteger(itemId, `${path}.itemId`);
            return priceStates.get(id) ?? Object.freeze({ state: "unknown" });
        },
    });
}

/**
 * Formats the effective typed item-price fact without re-resolving layering in
 * presentation code. Unknown and unavailable prices remain explicit labels.
 */
export function createCalculatorItemPriceTextResolver(projection, presentation) {
    const parsedPresentation = parsePresentation(presentation);
    const priceResolver = createCalculatorItemPriceStateResolver(projection);
    const formatter = new Intl.NumberFormat(
        parsedPresentation.locale,
        parsedPresentation.numberFormats.silver,
    );
    return Object.freeze({
        resolve(itemId, path = `projection.itemPrices.${itemId}`) {
            const id = requirePositiveInteger(itemId, `${path}.itemId`);
            return formatNumeric(
                priceResolver.resolve(id, path),
                "silver",
                formatter,
                parsedPresentation.labels,
                path,
            );
        },
    });
}

function rankedRows(
    rows,
    rankings,
    rowKey,
    rankingKey,
    path,
    { allowRankingsWithoutRows = false } = {},
) {
    const rowByKey = new Map();
    for (const row of rows) {
        const key = rowKey(row);
        if (rowByKey.has(key)) {
            throw new CalculatorPresentationContractError(path, `duplicate row key ${key}`);
        }
        rowByKey.set(key, row);
    }
    const rankByKey = new Map();
    const claimedRanks = new Set();
    requirePresentationArray(rankings, path).forEach((ranking, index) => {
        const entryPath = `${path}[${index}]`;
        const record = requireRecord(ranking, entryPath);
        const key = rankingKey(record, entryPath);
        if (!Number.isInteger(record.rank) || record.rank < 0) {
            throw new CalculatorPresentationContractError(`${entryPath}.rank`, "expected a non-negative integer");
        }
        if (rankByKey.has(key) || claimedRanks.has(record.rank)) {
            throw new CalculatorPresentationContractError(path, "keys and ranks must be unique");
        }
        rankByKey.set(key, record.rank);
        claimedRanks.add(record.rank);
    });
    for (const key of rankByKey.keys()) {
        if (!allowRankingsWithoutRows && !rowByKey.has(key)) {
            throw new CalculatorPresentationContractError(path, `rank references missing row ${key}`);
        }
    }
    return Array.from(rowByKey, ([key, row]) => {
        if (!rankByKey.has(key)) {
            throw new CalculatorPresentationContractError(path, `missing rank for ${key}`);
        }
        return { row, rank: rankByKey.get(key) };
    })
        .sort((left, right) => left.rank - right.rank)
        .map(({ row }) => row);
}

function groupOrderKey(record, path) {
    return requireString(record, "groupId", path);
}

function itemOrderKey(record, path) {
    const groupId = requireString(record, "groupId", path);
    return `${groupId}:${requirePositiveInteger(record.itemId, `${path}.itemId`)}`;
}

function buildLootSankeyViewFromContext(context, rightMetric, lootRateDisplayMode) {
    const displayMode = parseCalculatorLootRateDisplayMode(
        lootRateDisplayMode,
        "lootRateDisplayMode",
    );
    const rightMetricDefinition = RIGHT_METRICS[rightMetric];
    if (!rightMetricDefinition) {
        throw new CalculatorPresentationContractError(
            "rightMetric",
            "expected profit, fishingExperience, or totemExperience",
        );
    }
    const {
        formatters,
        metricPresentations,
        order,
        presentation,
        priceResolver,
        projectionRecord,
        sankey,
    } = context;
    const leftMetricUnitMark = calculatorMetricUnitMarkForValueField("expectedCount");
    const rightMetricUnitMark = calculatorMetricUnitMarkForValueField(
        rightMetricDefinition.field,
    );

    const groupRows = requirePresentationArray(sankey.groups, "projection.sankey.groups")
        .map((row, index) => {
            const path = `projection.sankey.groups[${index}]`;
            const record = requireRecord(row, path);
            const rowId = requireString(record, "rowId", path);
            const groupId = requireString(record, "groupId", path);
            const role = requireString(record, "role", path);
            const rate = parseQuantifiedRate(record.rate, `${path}.rate`);
            const denormalizedRate = parseDenormalizedRate(
                record.denormalizedRate,
                `${path}.denormalizedRate`,
            );
            const rawWeight = parseRawWeightRate(record.rawWeight, `${path}.rawWeight`);
            const leftValue = parseNumericValue(record.expectedCount, `${path}.expectedCount`);
            const rightValue = parseNumericValue(
                record[rightMetricDefinition.field],
                `${path}.${rightMetricDefinition.field}`,
            );
            const leftMetric = metricPresentationFor(
                metricPresentations,
                "groups",
                rowId,
                "expectedCount",
                `${path}.metrics.expectedCount`,
            );
            const rightMetricPresentation = metricPresentationFor(
                metricPresentations,
                "groups",
                rowId,
                rightMetricDefinition.field,
                `${path}.metrics.${rightMetricDefinition.field}`,
            );
            const groupProvenance = context.groupProvenance(
                record.traceIds,
                groupId,
                role,
                path,
            );
            return Object.freeze({
                rowId,
                groupId,
                role,
                rateWeight: rate.value,
                leftWeight: numericWeight(leftValue),
                rightWeight: numericWeight(rightValue),
                rateDisplay: formatLootRateForMode(
                    { ...rate, state: "known" },
                    denormalizedRate,
                    rawWeight,
                    displayMode,
                    presentation,
                ),
                leftShareDisplay: formatMetricShare(
                    leftMetric.share,
                    presentation,
                    `${path}.metrics.expectedCount.share`,
                ),
                leftText: formatNumeric(
                    leftValue,
                    "count",
                    formatters.count,
                    presentation.labels,
                    `${path}.expectedCount`,
                ),
                rightText: formatNumeric(
                    rightValue,
                    rightMetricDefinition.unit,
                    formatters[rightMetricDefinition.format],
                    presentation.labels,
                    `${path}.${rightMetricDefinition.field}`,
                ),
                rightShareDisplay: formatMetricShare(
                    rightMetricPresentation.share,
                    presentation,
                    `${path}.metrics.${rightMetricDefinition.field}.share`,
                ),
                rightCompactText: formatCompactNumeric(
                    rightValue,
                    rightMetricDefinition.unit,
                    presentation.locale,
                    presentation.labels,
                    `${path}.${rightMetricDefinition.field}`,
                ),
                leftBreakdown: leftMetric.breakdown,
                rightBreakdown: rightMetricPresentation.breakdown,
                conditionText: groupProvenance.conditionText,
                provenanceSegments: groupProvenance.segments,
                ...context.groupPresentation(groupId),
            });
        });
    const groups = rankedRows(
        groupRows,
        order.groups,
        (row) => row.groupId,
        groupOrderKey,
        "projection.presentationOrder.groups",
        // Group order covers every active group. An active group with an
        // unknown or unavailable top-level rate is deliberately absent from
        // quantified Sankey rows and remains inspector-only.
        { allowRankingsWithoutRows: true },
    );
    const groupById = new Map(groups.map((row) => [row.groupId, row]));

    const quantifiedItemInputs = requirePresentationArray(
        sankey.items,
        "projection.sankey.items",
    ).map((row, index) => ({
        quantified: true,
        path: `projection.sankey.items[${index}]`,
        row,
    }));
    const unquantifiedItemInputs = requirePresentationArray(
        projectionRecord.unquantifiedItems,
        "projection.unquantifiedItems",
    ).flatMap((row, index) => {
        const path = `projection.unquantifiedItems[${index}]`;
        const record = requireRecord(row, path);
        const groupId = requirePresentationNullableString(
            record.groupId,
            `${path}.groupId`,
        );
        const itemId = requirePositiveInteger(record.itemId, `${path}.itemId`);
        const presence = requireString(record, "presence", path);
        if (
            presence !== "present"
            || groupId === null
            || !groupById.has(groupId)
            || !Object.prototype.hasOwnProperty.call(
                presentation.itemsByItemId,
                String(itemId),
            )
        ) {
            return [];
        }
        return [{ quantified: false, path, row }];
    });
    const itemRows = [...quantifiedItemInputs, ...unquantifiedItemInputs]
        .map(({ quantified, path, row }) => {
            const record = requireRecord(row, path);
            const rowId = requireString(record, "rowId", path);
            const groupId = requireString(record, "groupId", path);
            const itemId = requirePositiveInteger(record.itemId, `${path}.itemId`);
            const group = groupById.get(groupId);
            if (!group) {
                throw new CalculatorPresentationContractError(`${path}.groupId`, "not a quantified group");
            }
            const rate = quantified
                ? { ...parseQuantifiedRate(record.rate, `${path}.rate`), state: "known" }
                : parseStatefulRate(record.rate, `${path}.rate`, "ratio", "item rate");
            const denormalizedRate = parseDenormalizedRate(
                record.denormalizedRate,
                `${path}.denormalizedRate`,
            );
            const rawWeight = parseRawWeightRate(record.rawWeight, `${path}.rawWeight`);
            const leftValue = quantified
                ? parseNumericValue(record.expectedCount, `${path}.expectedCount`)
                : { state: "unknown" };
            const expectedProfit = quantified
                ? parseNumericValue(record.expectedProfit, `${path}.expectedProfit`)
                : { state: "unknown" };
            const expectedFishingExperience = quantified
                ? parseNumericValue(
                    record.expectedFishingExperience,
                    `${path}.expectedFishingExperience`,
                )
                : { state: "unknown" };
            const expectedTotemExperience = quantified
                ? parseNumericValue(
                    record.expectedTotemExperience,
                    `${path}.expectedTotemExperience`,
                )
                : { state: "unknown" };
            const rightValue = quantified
                ? parseNumericValue(
                    record[rightMetricDefinition.field],
                    `${path}.${rightMetricDefinition.field}`,
                )
                : { state: "unknown" };
            const leftMetric = quantified
                ? metricPresentationFor(
                    metricPresentations,
                    "items",
                    rowId,
                    "expectedCount",
                    `${path}.metrics.expectedCount`,
                )
                : { breakdown: null, share: { state: "unknown" } };
            const rightMetricPresentation = quantified
                ? metricPresentationFor(
                    metricPresentations,
                    "items",
                    rowId,
                    rightMetricDefinition.field,
                    `${path}.metrics.${rightMetricDefinition.field}`,
                )
                : { breakdown: null, share: { state: "unknown" } };
            const provenanceSegments = context.itemProvenance(
                record.traceIds,
                itemId,
                groupId,
                rate.state === "known" && rate.value === 0,
                path,
                !quantified,
            );
            const quantityProvenanceRelevant = provenanceSegments.some((segment) => (
                segment.quantityProvenanceRelevant === true
            ));
            const quantityDisplay = quantityProvenanceRelevant
                ? calculatorItemQuantityDisplay(
                    record.quantity,
                    record.expectedQuantity,
                    { locale: presentation.locale, path },
                )
                : null;
            const itemPresentation = context.itemPresentation(itemId);
            const priceValue = priceResolver.resolve(itemId, path);
            return Object.freeze({
                rowId,
                groupRowId: group.rowId,
                groupId,
                itemId,
                discarded: quantified
                    ? requirePresentationBoolean(record.discarded, `${path}.discarded`)
                    : null,
                rateWeight: rate.state === "known" ? rate.value : null,
                leftWeight: numericWeight(leftValue),
                rightWeight: numericWeight(rightValue),
                rateDisplay: formatLootRateForMode(
                    rate,
                    denormalizedRate,
                    rawWeight,
                    displayMode,
                    presentation,
                    { itemRate: true },
                ),
                leftMetricUnitMark,
                leftText: formatNumeric(
                    leftValue,
                    "count",
                    formatters.count,
                    presentation.labels,
                    `${path}.expectedCount`,
                ),
                rightText: formatNumeric(
                    rightValue,
                    rightMetricDefinition.unit,
                    formatters[rightMetricDefinition.format],
                    presentation.labels,
                    `${path}.${rightMetricDefinition.field}`,
                ),
                rightShareDisplay: formatMetricShare(
                    rightMetricPresentation.share,
                    presentation,
                    `${path}.metrics.${rightMetricDefinition.field}.share`,
                ),
                rightMetricUnitMark,
                rightCompactText: formatCompactNumeric(
                    rightValue,
                    rightMetricDefinition.unit,
                    presentation.locale,
                    presentation.labels,
                    `${path}.${rightMetricDefinition.field}`,
                ),
                leftBreakdown: leftMetric.breakdown,
                rightBreakdown: rightMetricPresentation.breakdown,
                priceText: formatNumeric(
                    priceValue,
                    "silver",
                    formatters.silver,
                    presentation.labels,
                    `${path}.price`,
                ),
                quantityText: quantityDisplay?.text ?? null,
                quantityLabel: quantityDisplay?.ariaLabel ?? null,
                provenanceSegments,
                groupFillColor: group.fillColor,
                groupStrokeColor: group.strokeColor,
                groupTextColor: group.textColor,
                groupConnectorColor: group.connectorColor,
                sortValueInputs: Object.freeze({
                    price: priceValue,
                    grade: {
                        state: "known",
                        value: itemPresentation.gradeRank,
                    },
                    dropRate: {
                        state: rate.state,
                        ...(rate.state === "known" ? { value: rate.value } : {}),
                    },
                    expectedValue: expectedProfit,
                    fishingExperience: expectedFishingExperience,
                    totemExperience: expectedTotemExperience,
                    evidence: calculatorLootEvidenceSortValue(provenanceSegments),
                    itemId: { state: "known", value: itemId },
                    label: itemPresentation.label,
                }),
                ...itemPresentation,
            });
        });
    const rankedItems = rankedRows(
        itemRows,
        order.items,
        (row) => `${row.groupId}:${row.itemId}`,
        itemOrderKey,
        "projection.presentationOrder.items",
        { allowRankingsWithoutRows: true },
    );
    const items = rankedItems.map((row, databaseRank) => {
        const { sortValueInputs, ...presentedRow } = row;
        return Object.freeze({
            ...presentedRow,
            sortValues: calculatorLootRowSortValues({
                ...sortValueInputs,
                database: { state: "known", value: databaseRank },
            }),
        });
    });
    const rowIds = new Set();
    for (const row of [...groups, ...items]) {
        if (rowIds.has(row.rowId)) {
            throw new CalculatorPresentationContractError(
                "projection.sankey",
                `duplicate rowId ${row.rowId}`,
            );
        }
        rowIds.add(row.rowId);
    }

    return Object.freeze({
        groups,
        items,
        labels: Object.freeze({
            ariaLabel: presentation.labels[rightMetricDefinition.ariaLabel],
            basePriceLabel: presentation.labels.basePriceLabel,
            leftMetricLabel: presentation.labels.countMetricLabel,
            provenanceLabel: presentation.labels.provenanceLabel,
            rateBasisLabels: presentation.labels.rateBasisLabels,
            rightMetricLabel: presentation.labels[rightMetricDefinition.label],
        }),
    });
}

/**
 * Creates one projector for a typed calculator projection. The projector owns
 * invariant Sankey presentation work so the three right-metric variants share
 * one parsed presentation, formatter set, price resolver, trace resolver, and
 * semantic provenance/static row projection.
 */
export function createLootSankeyProjector(
    projection,
    presentationValue,
    metricPresentations,
    projectorCache = null,
) {
    const projectionRecord = requireRecord(projection, "projection");
    const sankey = requireRecord(projectionRecord.sankey, "projection.sankey");
    const order = requireRecord(projectionRecord.presentationOrder, "projection.presentationOrder");
    const presentation = parsePresentation(presentationValue);
    const priceResolver = createCalculatorItemPriceStateResolver(projectionRecord);
    const formatters = Object.freeze({
        count: new Intl.NumberFormat(presentation.locale, presentation.numberFormats.count),
        experience: new Intl.NumberFormat(
            presentation.locale,
            presentation.numberFormats.experience,
        ),
        silver: new Intl.NumberFormat(presentation.locale, presentation.numberFormats.silver),
    });
    const context = createLootSankeyProjectionContext({
        formatters,
        metricPresentations,
        order,
        parseGroupPresentation: (groupId) => parseStaticGroupPresentation(
            presentation.groupsByGroupId,
            groupId,
        ),
        parseItemPresentation: (itemId) => parseStaticItemPresentation(
            presentation.itemsByItemId,
            itemId,
            presentation.unknownItem,
            presentation.labels.unknownItemLabel,
        ),
        presentation,
        priceResolver,
        projectionRecord,
        projectorCache,
        sankey,
    });
    return Object.freeze({
        build(rightMetric, lootRateDisplayMode = "normalized") {
            return buildLootSankeyViewFromContext(
                context,
                rightMetric,
                lootRateDisplayMode,
            );
        },
    });
}

/**
 * Builds a D3 view from CalculatorProjectionV1 and static presentation metadata.
 * Scenario-varying values and trace segments are always projected from the typed
 * engine result. Source-backed present `unquantifiedItems` with an exact active
 * group remain visible as reference rows, but receive no numeric flow weight.
 *
 * Callers that build more than one metric variant should use
 * `createLootSankeyProjector` so invariant projection work is shared.
 */
export function buildLootSankeyView(
    projection,
    presentationValue,
    rightMetric,
    metricPresentations,
    lootRateDisplayMode = "normalized",
) {
    return createLootSankeyProjector(
        projection,
        presentationValue,
        metricPresentations,
    ).build(rightMetric, lootRateDisplayMode);
}

export { createLootSankeyProjectorCache };
